import Vue from 'vue';
import ToolService from '@/providers/tools';
import Component from 'vue-class-component';
import { Prop, Watch } from 'vue-property-decorator';
import { Validations } from 'vuelidate-property-decorators';

@Component({})
class EdTextarea extends Vue {
  @Prop({default: ' '}) readonly placeholder!: string;
  @Prop() readonly maxlength!: number;
  @Prop({ type: Boolean, default: false }) readonly readonly!: boolean;
  @Prop({ type: Boolean, default: false }) readonly disabled!: boolean;
  @Prop({ type: Boolean, default: false }) readonly clearable!: boolean;
  @Prop({ type: Boolean, default: false }) readonly labelFloating!: boolean;
  @Prop({ type: Boolean, default: false }) readonly positiveNumeric!: boolean;
  @Prop() readonly label!: string;
  @Prop() readonly counter!: number;
  @Prop() readonly messages!: string[];
  @Prop({default: 1}) readonly rows: number;

  value: string = '';
  isFocused: boolean = false;
  private isValidText: boolean = false;

  @Validations()
  validations() {
    const attrs: any = this.$attrs;
    return {
      value: attrs?.value?.validators ?? {}
    };
  }

  @Watch('value')
  resetValidation() {
    this.isValidText = !this.$v.value.maxLength || !this.$v.value.url;
  }

  get configStyles() {
    return {
      '--ed-indicator-color': this.isInvalid ? 'var(--ed-color-danger)':'var(--ed-color-green)',
      '--ed-counter-color': this.isInvalid ? 'var(--ed-color-danger)':'var(--ed-color-medium)',
      '--ed-border-color-active': this.isInvalid ? 'var(--ed-color-danger)': this.borderColor,
      '--ed-padding-clearable': this.clearable ? '0 32px 0 4px' : '0 0 4px',
      '--ed-messages-support-bottom-position': this.labelFloating ? '2px' : '-12px',
      '--ed-error-messages-bottom-position': this.labelFloating ? '2px' : '-12px',
    };
  }

  get borderColor() {
    return this.isFocused? 'var(--ed-color-green)' : 'var(--ed-color-medium)';
  }

  get errorMessages(): string[] {
    const messages = [];
    if(this.isRequiredError) {
      messages.push(this.$i18n.t('errors.required'));
    }
    if(this.isMaxLengthError) {
      messages.push(this.$i18n.t('errors.maxLength'));
    }
    if(this.isUrlError) {
      messages.push(this.$i18n.t('errors.url'));
    }
    return messages;
  }

  get isInvalid() {
    return this.isValidText && this.$v.value.$invalid;
  }

  get isMaxLengthError() {
    if(this.$v.value.$params.maxLength && this.isInvalid) {
      return !this.$v.value.maxLength;
    }
    return false;
  }

  get isUrlError() {
    if(this.$v.value.$params.url && this.isInvalid) {
      return !this.$v.value.url;
    }
    return false;
  }

  get isRequiredError() {
    if(this.$v.value.$params.required && this.isInvalid) {
      return !this.$v.value.required;
    }
    return false;
  }

  clearValue() {
    if (this.disabled || this.readonly) {
      return;
    }
    
    this.value = '';
    this.setValue();
  }

  onKeyUp(): void {
    if(this.positiveNumeric) {
      let value = '';
      let regex = /^[0-9]*$/
      let parts = this.value.split('');
      parts.forEach(l => {
        if(regex.test(l)) {
          value += l;
        }
      })
      this.value = value;
    }
    this.setValue();
  }

  setValue() {
    this.$emit('input', {
      value: this.value,
      valid: !this.$v.value.$invalid,
      validators: this.validators
    });
  }

  get validators() {
    let input: any = this.$attrs.value;
    return input ? input.validators : {};
  }

  setBlur(): void {
    this.isFocused = false;
    this.$emit('blur');
  }

  setFocus(): void {
    this.isFocused = true;
    this.$emit('focus');
  }

  keyUpEnter(): void {
    this.$emit('enter');
  }

  created() {
    const textarea = this.$attrs.value as any;

    if(textarea) {
      textarea.validators = textarea.validators ?? {};
      this.value = textarea.value;
      this.setValue();
    }
  }

  @Watch('$attrs')
  attrsChanged() {
    let input: any = this.$attrs.value;
    let newValue = input?.value ?? '';
    const processedValue = ToolService.exitSpecialChars(newValue);

    if (this.value !== processedValue) {
      this.value = processedValue;
      this.setValue();
    }
  }
}

export default Vue.component('ed-textarea', EdTextarea);